import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import BackLink from "@/components/BackLink";
import usePersistedState from "@/lib/usePersistedState";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Download, FileText } from "lucide-react";
import { inr, formatDate, csvRow } from "@/lib/utils2";

export default function PartyLedger() {
  const nav = useNavigate();
  const { id } = useParams();
  const [party, setParty] = useState(null);
  const [billsDetail, setBillsDetail] = useState({ bills: [], on_account: 0 });
  const [payments, setPayments] = useState([]);
  const [typeFilter, setTypeFilter] = usePersistedState("filters:pledger:type", "all");

  useEffect(() => { (async () => {
    const [p, bd, pm] = await Promise.all([
      api.get("/parties"),
      api.get(`/party/${id}/bills-detail`),
      api.get("/payments-received", { params: { party_id: id } }),
    ]);
    setParty(p.data.find((x) => x.id === id));
    setBillsDetail(bd.data);
    setPayments(pm.data);
  })(); }, [id]);

  const { bills: allBills, on_account } = billsDetail;
  const bills = typeFilter === "all" ? allBills : allBills.filter((b) => b.bill_type === typeFilter);

  const totals = {
    billed: allBills.reduce((s, b) => s + b.total_amount, 0),
    received: allBills.reduce((s, b) => s + b.received_total, 0) + on_account,
    balance: allBills.reduce((s, b) => s + b.balance_due, 0) - on_account,
  };

  // Running ledger — use ALL bills regardless of filter
  const rows = [
    ...allBills.map((b) => ({ date: b.bill_date, kind: `${b.bill_type === "material" ? "Material" : "Transport"} Bill`, ref: b.bill_number, debit: b.total_amount, credit: 0, link: `/bills/${b.id}` })),
    ...payments.map((p) => ({ date: p.date, kind: "Payment", ref: `${p.mode}${p.particulars ? ` · ${p.particulars}` : ""}`, debit: 0, credit: p.amount })),
  ].sort((a, b) => (a.date < b.date ? -1 : 1));
  let running = 0;
  const withRunning = rows.map((r) => { running += (r.debit || 0) - (r.credit || 0); return { ...r, balance: running }; });

  const exportCsv = () => {
    const headers = ["Date", "Particulars", "Reference", "Debit", "Credit", "Running Balance"];
    const data = withRunning.map((r) => [r.date, r.kind, r.ref, r.debit, r.credit, r.balance]);
    const csv = [headers, ...data].map(csvRow).join("");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `ledger-${party?.name?.replace(/\s+/g, "_") || "party"}-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1300px] mx-auto" data-testid="party-ledger-page">
      <div className="flex items-center justify-between mb-4">
        <BackLink to="/party-ledgers" label="Back" testId="party-ledger-back" />
        <div className="flex items-center gap-2">
        <Button onClick={exportCsv} data-testid="export-party-ledger-btn" className="rounded-sm bg-brand-forest text-white">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
        <Link to={`/party-statement/${id}`}>
          <Button data-testid="open-party-statement-btn" variant="outline" className="rounded-sm border-brand-ink">
            <FileText className="h-4 w-4 mr-1.5" /> Party Statement
          </Button>
        </Link>
        </div>
      </div>
      <PageHeader title={party?.name || "Party Ledger"} subtitle={`GSTIN: ${party?.gstin || "—"} · ${party?.address || ""}`} />

      <div className="grid grid-cols-4 gap-4 mb-8">
        <KPI label="Total Billed" value={inr(totals.billed)} />
        <KPI label="Total Received" value={inr(totals.received)} accent="text-brand-forest" />
        <KPI label="On Account" value={inr(on_account)} accent="text-brand-sand" />
        <KPI label="Balance Due" value={inr(totals.balance)} accent={totals.balance > 0 ? "text-brand-terracotta" : "text-brand-forest"} />
      </div>

      {/* Bill-wise breakdown */}
      <Card className="rounded-sm border-brand-line shadow-none mb-8 overflow-hidden">
        <div className="px-5 py-4 border-b border-brand-line flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="font-serif text-xl text-brand-ink">Bill-wise Breakdown</div>
            <div className="text-xs text-brand-muted">Material & Transport bills shown together, sorted by date · {bills.length} of {allBills.length} shown</div>
          </div>
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="mt-1 w-40 h-9 rounded-sm bg-white" data-testid="party-ledger-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All (Both)</SelectItem>
                  <SelectItem value="material">Material Only</SelectItem>
                  <SelectItem value="transport">Transport Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-2.5">Bill No</th>
              <th className="text-left px-3 py-2.5">Date</th>
              <th className="text-left px-3 py-2.5">Type</th>
              <th className="text-right px-3 py-2.5">Subtotal</th>
              <th className="text-right px-3 py-2.5">GST</th>
              <th className="text-right px-3 py-2.5">Total</th>
              <th className="text-right px-3 py-2.5">Received</th>
              <th className="text-right px-3 py-2.5">Balance</th>
              <th className="text-center px-3 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {bills.length === 0 && <tr><td colSpan={9} className="px-5 py-8 text-center text-brand-muted text-sm">No bills match filter.</td></tr>}
            {bills.map((b) => (
              <tr key={b.id} className="border-t border-brand-line hover:bg-secondary/40 cursor-pointer"
                onClick={() => nav(`/bills/${b.id}`)}
                data-testid={`bill-detail-row-${b.id}`}>
                <td className="px-5 py-3 font-mono text-xs text-brand-forest hover:underline">{b.bill_number}</td>
                <td className="px-3 py-3">{formatDate(b.bill_date)}</td>
                <td className="px-3 py-3">
                  <Badge variant="outline" className={`rounded-sm uppercase text-[10px] tracking-[0.1em] ${b.bill_type === "material" ? "border-brand-forest text-brand-forest" : "border-brand-sand text-brand-sand"}`}>
                    {b.bill_type}
                  </Badge>
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{inr(b.subtotal)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-xs">{b.gst_percent}%</td>
                <td className="px-3 py-3 text-right tabular-nums">{inr(b.total_amount)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-brand-forest">{inr(b.received_total)}</td>
                <td className={`px-3 py-3 text-right tabular-nums font-medium ${b.balance_due > 0 ? "text-brand-terracotta" : "text-brand-muted"}`}>{inr(b.balance_due)}</td>
                <td className="px-3 py-3 text-center">
                  <Badge className={`rounded-sm text-[10px] tracking-[0.1em] ${
                    b.status === "SETTLED" ? "bg-brand-forest text-white" :
                    b.status === "PARTIAL" ? "bg-brand-sand/30 text-brand-terracotta" :
                    "bg-brand-terracotta/10 text-brand-terracotta border-brand-terracotta"
                  }`}>{b.status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Running ledger */}
      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <div className="px-5 py-4 border-b border-brand-line">
          <div className="font-serif text-xl text-brand-ink">Running Ledger</div>
          <div className="text-xs text-brand-muted">Chronological debits and credits with running balance</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-2.5">Date</th>
              <th className="text-left px-3 py-2.5">Particulars</th>
              <th className="text-left px-3 py-2.5">Ref</th>
              <th className="text-right px-3 py-2.5">Debit</th>
              <th className="text-right px-3 py-2.5">Credit</th>
              <th className="text-right px-5 py-2.5">Balance</th>
            </tr>
          </thead>
          <tbody>
            {withRunning.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-brand-muted text-sm">No transactions yet.</td></tr>}
            {withRunning.map((r, i) => (
              <tr key={i} className="border-t border-brand-line">
                <td className="px-5 py-2.5 whitespace-nowrap">{formatDate(r.date)}</td>
                <td className="px-3 py-2.5">{r.kind}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{r.link ? <Link to={r.link} className="hover:text-brand-forest hover:underline">{r.ref}</Link> : r.ref}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{r.debit ? inr(r.debit) : "—"}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-brand-forest">{r.credit ? inr(r.credit) : "—"}</td>
                <td className="px-5 py-2.5 text-right tabular-nums font-medium">{inr(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <Card className="rounded-sm border-brand-line shadow-none p-5">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">{label}</div>
      <div className={`font-serif text-2xl mt-2 tabular-nums ${accent || "text-brand-ink"}`}>{value}</div>
    </Card>
  );
}
